import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Field, RegisterFieldRequest, LoginFieldRequest } from '../types/field';
import { universalAuthService } from '../services/universalAuthService';
import { useTokenRefresh } from '../hooks/useTokenRefresh';

interface FieldContextType {
  field: Field | null;
  loading: boolean;
  error: string | null;
  initializing: boolean;
  isFieldAuthenticated: boolean;
  loginField: (email: string, password: string) => Promise<void>;
  registerField: (request: RegisterFieldRequest) => Promise<void>;
  logoutField: () => void;
  refreshFieldData: () => Promise<void>;
}

const FieldContext = createContext<FieldContextType | undefined>(undefined);

// Функции для работы с данными поля через универсальный сервис
const saveFieldData = (field: Field, token: string, refreshToken?: string) => {
  universalAuthService.saveData('field', field, token, refreshToken);
};

const clearFieldData = () => {
  universalAuthService.clearData('field');
};

export const FieldProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [field, setField] = useState<Field | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [initializing, setInitializing] = useState<boolean>(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Инициализируем автоматическое обновление токенов для field
  useTokenRefresh('field');

  // Инициализация при старте приложения (только один раз)
  useEffect(() => {
    let isMounted = true;

    const initializeFieldAuth = async () => {
      try {
        console.log('Initializing field auth...');
        // Проверяем было ли поле залогинено до инициализации
        const hadStoredData = !!localStorage.getItem('fieldData');
        const fieldData = await universalAuthService.initializeAuth('field');

        if (!isMounted) return;

        if (fieldData) {
          setField(fieldData);
          console.log('Field set successfully');
          // Редирект после логина происходит в AuthContext через window.location.href
        } else {
          console.log('No valid field data found');
          // Если были данные но токен истёк - редирект на главную
          if (hadStoredData && location.pathname !== '/') {
            console.log('Field token expired, redirecting to homepage');
            navigate('/');
          }
        }
      } catch (error) {
        console.error('Error initializing field auth:', error);
        clearFieldData();
      } finally {
        if (isMounted) {
          setInitializing(false);
        }
      }
    };

    initializeFieldAuth();

    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Только при монтировании

  const logoutField = useCallback(() => {
    setField(null);
    setError(null);
    clearFieldData();
    navigate('/');
  }, [navigate]);

  const refreshFieldData = useCallback(async () => {
    if (!field?.id) {
      return;
    }

    try {
      const { getFieldProfile } = await import('../services/fieldService');
      const fieldProfile = await getFieldProfile();
      setField(fieldProfile);
      
      // Обновляем данные в localStorage
      const { token } = universalAuthService.getStoredData('field');
      if (token) {
        saveFieldData(fieldProfile, token);
      }
    } catch (error: any) {
      console.error('Error refreshing field data:', error);
      // Silent failure for refresh, как в AuthContext
    }
  }, [field]);

  const loginField = useCallback(async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const { loginField: loginFieldService } = await import('../services/fieldService');
      
      const response = await loginFieldService({ email, password });
      
      // Используем данные поля из ответа на логин (унифицированное API)
      const fieldProfile = response.fieldData;

      // Сохраняем токен, refreshToken и данные поля
      saveFieldData(fieldProfile, response.token, response.refreshToken);

      // Устанавливаем поле
      setField(fieldProfile);

      // Перенаправляем на унифицированную страницу поля
      navigate(`/fields/${fieldProfile.id}`);
    } catch (error: any) {
      const errorMessage = error.message || 'Login failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const registerField = useCallback(async (request: RegisterFieldRequest) => {
    setLoading(true);
    setError(null);
    
    try {
      const { registerField: registerFieldService } = await import('../services/fieldService');
      
      await registerFieldService(request);
      // После регистрации не авторизуем автоматически, пользователь должен подтвердить email
    } catch (error: any) {
      setError(error.message || 'Registration failed');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const isFieldAuthenticated = !!field;

  const value: FieldContextType = {
    field,
    loading,
    error,
    initializing,
    isFieldAuthenticated,
    loginField,
    registerField,
    logoutField,
    refreshFieldData,
  };

  return (
    <FieldContext.Provider value={value}>
      {children}
    </FieldContext.Provider>
  );
};

export const useFieldAuth = (): FieldContextType => {
  const context = useContext(FieldContext);
  if (!context) {
    throw new Error('useFieldAuth must be used within a FieldProvider');
  }
  return context;
};